import React, { FC } from "react";
import { Box, Text } from "@primer/react";
import { Banner } from "@primer/react/experimental";
import { BaseDialog } from "@getregistered/greg-editors/dist/lib/primer-ui/BaseDialog/BaseDialog";
import {
  InvitationStatusBreakdown,
  firstSendCount,
  resendCount,
  sendableCount,
} from "./InvitationStatusContent";

interface InvitationSendModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectionCount: number;
  preflightCounts?: Record<string, number>;
}

// Keep in sync with BulkActions::InvitationSendExecutor#skip_reason.
const describeSendRow = (status: string) => {
  switch (status) {
    case "pending":
      return "Will be sent for the first time";
    case "sent":
    case "accepted":
      return "Will be resent";
    case "delivering":
      return "Already being sent — will be skipped";
    case "declined":
      return "Invitation was declined — will be skipped";
    case "registered":
      return "Attendee has already registered — will be skipped";
    default:
      return "Will be skipped";
  }
};

// Describe the send/resend split as a single sentence: pending invitations are
// first sends, already-sent/accepted ones are resends. Falls back to the raw
// selection count until preflight resolves.
const describeMix = (counts: Record<string, number> | undefined, selectionCount: number) => {
  if (!counts) return `${selectionCount} ${selectionCount === 1 ? "invitation" : "invitations"}`;

  const firstSend = firstSendCount(counts);
  const resend = resendCount(counts);

  if (firstSend > 0 && resend > 0) {
    return `${firstSend} will be sent for the first time, ${resend} resent`;
  }
  if (resend > 0) {
    return `${resend} ${resend === 1 ? "invitation" : "invitations"} will be resent`;
  }
  return `${firstSend} ${firstSend === 1 ? "invitation" : "invitations"} will be sent`;
};

export const InvitationSendModal: FC<InvitationSendModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectionCount,
  preflightCounts,
}) => {
  // Until preflight resolves, assume the whole selection is sendable so the
  // button isn't disabled on a slow network.
  const sendable = preflightCounts ? sendableCount(preflightCounts) : selectionCount;
  const nothingSendable = sendable === 0;
  const rightButtonText = `Send / Resend ${sendable} ${sendable === 1 ? "invitation" : "invitations"}`;

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Send / Resend Invitations"
      position="center"
      width="600px"
      leftButtonText="Go back"
      rightButtonText={rightButtonText}
      rightButtonVariant="primary"
      rightButtonDisabled={nothingSendable}
      onLeftButtonClick={onClose}
      onRightButtonClick={nothingSendable ? undefined : onConfirm}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {nothingSendable ? (
          <Banner
            variant="warning"
            title={`None of the selected ${selectionCount === 1 ? "invitation" : "invitations"} can be sent. Declined, already-registered and in-flight invitations are skipped.`}
          />
        ) : (
          <Text as="div" fontSize={1}>
            <Text as="span" fontWeight="bold">{describeMix(preflightCounts, selectionCount)}</Text>
          </Text>
        )}

        {preflightCounts && (
          <InvitationStatusBreakdown counts={preflightCounts} describeRow={describeSendRow} />
        )}

        <Text as="div" fontSize={1}>
          Pending invitations are emailed for the first time; already-sent and accepted
          invitations are re-sent. Declined, registered and in-flight invitations are skipped.
        </Text>
      </Box>
    </BaseDialog>
  );
};
